import { FC, useEffect, useContext, useRef } from 'react'
import { useRouter } from 'next/router'
import statisticContext from 'context/statisticsContext'

import Auth from 'store/auth'
import { createQueryUnAuth } from './utils'
import { postAxiosSingle } from 'pages/api/axios'
import { TICK, YandexPlayerStatuses } from '../data'

import iframeStyle from '../iframe.module.scss'

interface IYandexVideo {
  videoLink: string
  materialId?: number | string
  materialType?: string
  noAnalytics?: boolean
  refPlayerOuter?: any
}

const YandexVideo: FC<IYandexVideo> = ({
  videoLink,
  materialId,
  materialType,
  noAnalytics,
  refPlayerOuter,
}: IYandexVideo) => {
  const playerRef = useRef<HTMLDivElement>(null)
  const socket = useContext(statisticContext)
  const interval = useRef<ReturnType<typeof setInterval> | null>(null)
  const { fakeAuth } = Auth.useAuth()
  const router = useRouter()
  const counterStartYa = useRef(0)

  const sendData = (eventName: string) => {
    if (Object.keys(socket).length) {
      socket?.sendEvent(eventName, {
        id: materialId,
        materialType: materialType,
        timestamp: Date.now(),
      })
    }
  }

  const handleStartVideo = () => {
    if (materialId && materialType) {
      sendData('playVideoRecord')

      if (counterStartYa.current === 0 && router.query.spec && fakeAuth)
        postAxiosSingle(createQueryUnAuth(router.query.spec), {}, { materialId, materialType })

      counterStartYa.current += 1

      interval.current = setInterval(() => {
        sendData('playVideoRecord')
      }, TICK)
    }
  }

  const handlePauseVideo = () => {
    if (interval?.current) {
      sendData('pauseVideoRecord')
      clearInterval(interval.current)
    }
  }

  useEffect(() => {
    if (socket.connect) {
      const script = document.createElement('script')
      script.src =
        'https://storage.yandexcloud.net/videoplatform-public/player/yandex-video-player-iframe-api-bundles/1.0-11944/js/player-sdk.js'
      script.async = true

      let yaInstance: any = null

      script.onload = () => {
        yaInstance = window.Ya.playerSdk.init({
          element: playerRef.current,
          source: videoLink,
          autoplay: false,
        })

        if (!yaInstance) return
        if (refPlayerOuter) refPlayerOuter.current = yaInstance
        if (!noAnalytics) {
          yaInstance.on('StatusChange', ({ status }: { status: YandexPlayerStatuses }) => {
            if (status === YandexPlayerStatuses.PLAY) {
              handleStartVideo()
            }

            if (status === YandexPlayerStatuses.PAUSE || status === YandexPlayerStatuses.END) {
              handlePauseVideo()
            }
          })
        }
      }

      document.body.appendChild(script)

      return () => {
        if (yaInstance) yaInstance.destroy()
        document.body.removeChild(script)
        handlePauseVideo()
      }
    }
  }, [socket.connect])

  return <div ref={playerRef} className={iframeStyle.inner} />
}

export default YandexVideo
